import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  BriefcaseIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import jobService from '../services/jobService';
import { Job } from '../types/job';
import { getApplyUrl } from '../utils/config';

const JobDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchJob = async () => {
      if (!id) return;
      
      try {
        const data = await jobService.getJobById(parseInt(id));
        setJob(data);
        setError(null);
      } catch (err) {
        setError('Failed to load job details. Please try again later.');
        console.error('Error fetching job:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [id]);

  const workSettingLabel = {
    REMOTE: 'Remote',
    ONSITE: 'On-site',
    HYBRID: 'Hybrid',
  };

  const handleShare = async () => {
    if (navigator.share && job) {
      try {
        await navigator.share({
          title: job.title,
          text: `Check out this job opportunity: ${job.title} at IST`,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled sharing
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8">
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12">
          <Link
            to="/jobs"
            className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 mb-8"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Jobs
          </Link>
          <div className="text-center py-16">
            <BriefcaseIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Job Not Found
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {error || "The job you're looking for doesn't exist or has been removed."}
            </p>
            <Link
              to="/jobs"
              className="inline-flex items-center px-6 py-3 text-base font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              View All Jobs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <section className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/jobs"
            className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 mb-6 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Jobs
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                  {job.title}
                </h1>
                <p className="text-lg text-primary-600 dark:text-primary-400 font-medium">
                  {job.department}
                </p>
              </div>
              <button
                onClick={handleShare}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <ShareIcon className="h-5 w-5 mr-2" />
                Share
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-gray-600 dark:text-gray-400">
              <span className="inline-flex items-center">
                <MapPinIcon className="h-5 w-5 mr-1.5" />
                {job.locations && job.locations.length > 0
                  ? job.locations.map((l) => l.displayName).join(', ')
                  : job.location}
              </span>
              <span className="inline-flex items-center">
                <ClockIcon className="h-5 w-5 mr-1.5" />
                {workSettingLabel[job.workSetting] || job.workSetting}
              </span>
              <span className="inline-flex items-center">
                <BriefcaseIcon className="h-5 w-5 mr-1.5" />
                {job.employmentType}
              </span>
              {job.salaryRange && (
                <span className="inline-flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 mr-1.5" />
                  {job.salaryRange}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                {job.employmentType}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                {workSettingLabel[job.workSetting] || job.workSetting}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                About this role
              </h2>
              
              {job.description ? (
                <div 
                  className="prose prose-gray dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: job.description }}
                />
              ) : (
                <p className="text-gray-600 dark:text-gray-400">
                  No description available for this position.
                </p>
              )}

              {job.skills && job.skills.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Required Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {job.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-1"
          >
            {/* Apply Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6 sticky top-24">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Interested in this role?
              </h3>
              <a
                href={getApplyUrl(job.id)}
                className="block w-full text-center px-6 py-3 text-base font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-lg mb-4"
              >
                Apply Now
              </a>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                By applying, you agree to our{" "}
                <Link
                  to="/privacy-policy"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  privacy policy
                </Link>
                .
              </p>
            </div>

            {/* Job Details Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Job Details
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Department</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{job.department}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {job.locations && job.locations.length > 1 ? 'Locations' : 'Location'}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {job.locations && job.locations.length > 0
                      ? job.locations.map((l) => l.displayName).join(', ')
                      : job.location}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Work Setting</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {workSettingLabel[job.workSetting] || job.workSetting}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Employment Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{job.employmentType}</dd>
                </div>
                {job.salaryRange && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Salary Range</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{job.salaryRange}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Posted Date</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {new Date(job.postedDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </dd>
                </div>
                {job.expirationDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Application Deadline</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                      {new Date(job.expirationDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsPage;
